import { compileFetchedEntity } from '../shared/compile-fetched-entity';
import { creatorId, creatorNode, creatorSavedChoice, creatorSkillGrants, creatorFeatAllowed, creatorSpellOptions, type PanelRow } from '../janus-engine/character-creator/panels';
import { decodeHtmlEntitiesForPlainText } from '../shared/decode-html-entities';
import { encounterCreatureField } from '../shared/array-types';
import { listDisplayContext, renderTrainingBookTier, renderAbilityListRead, renderSkillRollModifiers, LIST_DISPLAY_ABILITY_CONFIG } from './render-item-list-display';
import { renderSingleItem, renderCardListBlock, renderCompactCardListLayers, renderCustomListBlock, renderUserChoiceListRead,
    renderCardListItemLayerRead, entryRequirementResolver, type RenderCtx, type RenderBlob } from './primitives';
import { itemsCol, layerOf, layerEntriesOf, layerSlot, hasLayer, scalarLayerSlot, isUuid, ucwordsAndSpaceTs } from './helpers';
import { authoredSlotOptions } from './authored-slot-presentation';

export interface LegacyListInput {
    kind: string; arr: RenderBlob; layer: string | string[]; options: Record<string, unknown>;
    form?: boolean; pc_id?: string; class_level?: number | null;
}
interface FormInputs { catalog: PanelRow; choices: PanelRow[]; metadata: PanelRow; lists: Record<string, string[]> }
type ListHost = Window & { ftMountPcSheet?: (root?: ParentNode) => void; ftMountAbilityButtons?: (root?: ParentNode) => void;
    ftHydrateUserChoiceOptions?: (root: HTMLElement) => void };
const host = window as ListHost;
const object = (value: unknown): PanelRow => value && typeof value === 'object' && !Array.isArray(value) ? value as PanelRow : {};
const json = (value: unknown): string => JSON.stringify(value).replace(/</g, '\\u003c');
const choiceKeys = (node: unknown): string[] => [...new Set([...Object.keys(object(node)), ...Object.keys(object(object(node).item_layer_types))])]
    .filter(key => /^user_choice_\d+_/.test(key) && layerEntriesOf(node, key).length > 0);
const plainName = (node: unknown, fallback = ''): string => decodeHtmlEntitiesForPlainText(String(itemsCol(node, 'item_name') ?? fallback));

function optionsOf(options: Record<string, unknown>): Record<string, unknown> {
    return Object.fromEntries(Object.entries(options).map(([key, value]) => [key.replace(/_([a-z])/g, (_, c: string) => c.toUpperCase()), value]));
}

/** The read surface returns saved choices and raw candidate definitions; the browser selects. */
async function readFormInputs(input: LegacyListInput): Promise<FormInputs> {
    const source = String(itemsCol(input.arr, 'item_id') ?? '');
    const read = async (types: string[] = []): Promise<FormInputs> => {
        const response = await fetch(BASE_URL + 'options/api/creator_definition_panels_inc.php', {
            method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ ids: isUuid(source) ? [source] : [], character_id: input.pc_id ?? '', types }),
        });
        if (!response.ok) throw new Error('unported:creator-panels:definitions-unavailable:' + response.status);
        const data = await response.json();
        if (data.shape_v !== 1 || !data.catalog) throw new Error('unported:creator-panels:invalid-definition-surface');
        return data;
    };
    const first = await read();
    const types = new Set<string>();
    for (const node of [input.arr, ...Object.values(first.catalog)]) for (const key of choiceKeys(node)) {
        const entries = layerEntriesOf(node, key), spec = entries.length === 1 ? scalarLayerSlot(entries[0]) : '';
        if (!spec || isUuid(spec)) continue;
        const suffix = key.replace(/^user_choice_\d+_/, '');
        types.add(spec === 'essence_increase' ? 'essence' : spec.includes(':')
            ? suffix.includes('menta') ? 'menta' : suffix.includes('curse') ? 'curse' : 'spell' : spec);
    }
    if (!types.size) return first;
    const pools = await read([...types]);
    return { ...first, catalog: { ...first.catalog, ...pools.catalog }, lists: pools.lists };
}

const formReads = new Map<string, Promise<FormInputs>>();
function formInputs(input: LegacyListInput): Promise<FormInputs> {
    const key = `${input.pc_id ?? ''}:${itemsCol(input.arr, 'item_id') ?? ''}`;
    let pending = formReads.get(key);
    if (!pending) {
        pending = readFormInputs(input).finally(() => { formReads.delete(key); });
        formReads.set(key, pending);
    }
    return pending;
}

/** Preserve per-pick ids and the existing creator's selected-value and skill-grant readers. */
export function formChoiceHtml(ctx: RenderCtx, key: string, owner: string, exact: string, inputs: FormInputs,
    source: unknown = ctx.arr, seen = new Set<string>()): string {
    const entries = layerEntriesOf(source, key);
    if (!entries.length) return '';
    const suffix = key.replace(/^user_choice_\d+_/, '');
    const saved = creatorSavedChoice(inputs.choices, key, owner, exact);
    const first = scalarLayerSlot(entries[0]);
    const wildcard = entries.length === 1 && !isUuid(first);
    const essence = first === 'essence_increase' || suffix.startsWith('essence_')
        || (inputs.metadata.essences ?? []).some((row: PanelRow) => creatorId(itemsCol(row, 'item_id')) === creatorId(first));
    const catalog = { ...inputs.catalog, ...object(ctx.arr.catalog) };
    let choices: Array<{ id: string; name: string; node: PanelRow }>;
    if (wildcard && first.includes(':')) {
        choices = creatorSpellOptions(key, first, Object.values(catalog))
            .map(row => ({ id: row.item_id, name: `${row.level} | ${decodeHtmlEntitiesForPlainText(row.item_name)}`, node: object(catalog[row.item_id]) }));
    } else if (wildcard) {
        const type = first === 'essence_increase' ? 'essence' : first;
        choices = (inputs.lists[type] ?? []).map(id => ({ id, name: plainName(catalog[id], id), node: object(catalog[id]) }))
            .filter(choice => first !== 'essence_increase' || itemsCol(choice.node, 'variable_name') !== 'spirit');
    } else choices = entries.map(entry => {
        const id = creatorId(entry), node = creatorNode(entry, { ...ctx.arr, catalog });
        return { id, name: plainName(node, id), node };
    });
    const tiers: PanelRow[] = inputs.metadata.tiers ?? [];
    if (suffix === 'feat') {
        const state: PanelRow = {};
        for (const id of ['ancestry_1', 'ancestry_2', 'lineage', 'body', 'mind', 'soul', 'spirit', 'fortune'])
            state[id] = (document.getElementById(['body', 'mind', 'soul', 'spirit', 'fortune'].includes(id) ? `${id}_input_total` : id) as HTMLInputElement | null)?.value ?? '';
        document.querySelectorAll<HTMLInputElement>('[id^="input_hidden_skill_tier_"]').forEach(el => { state[el.id.replace('input_hidden_skill_tier_', '').toUpperCase()] = el.value; });
        choices = choices.filter(choice => creatorFeatAllowed(choice.node, state, tiers) || creatorId(choice.id) === creatorId(saved));
    }
    // Spell selector order is already level/name ordered by the shared native selector.
    if (!first.includes(':')) choices.sort((a, b) => a.name.localeCompare(b.name));
    if (saved && !choices.some(choice => creatorId(choice.id) === creatorId(saved)))
        choices.push({ id: saved, name: plainName(catalog[creatorId(saved)], `Saved choice (unavailable): ${saved}`), node: object(catalog[creatorId(saved)]) });
    const levelOf = (node: unknown): number => Number(layerOf(node, 'requires_level') ?? layerOf(node, 'requires_class_level') ?? 1);
    const level = Math.max(1, levelOf(ctx.arr), levelOf(source));
    const tier = tiers.find(row => row.variable_name === suffix);
    const grants = (node: unknown, id: string): string => {
        const rows = tier ? [{ s: id, t: Number(tier.skill_tier_level) }]
            : Object.entries(creatorSkillGrants(node, tiers)).map(([s, t]) => ({ s, t }));
        return ` data-ft-skill-grants="${escapeHtml(JSON.stringify(rows))}" data-ft-grant-level="${level}"`;
    };
    const family = essence ? suffix.endsWith('_ancestry') ? 'ancestral_essence_select' : suffix.endsWith('_background') ? 'background_essence_select'
        : suffix.endsWith('_feat') ? 'feat_essence_select' : 'essence_select' : suffix;
    const feat = suffix === 'feat', sourceId = exact || owner;
    const id = feat ? `${sourceId}_feat` : `${key}-${owner}_${exact}`;
    const name = `${key}[${owner}_${exact}]${feat ? '_feat' : ''}`;
    let html = `<li class="list-group-item fate-tide-sheet-ability-subrow"><select id="${escapeHtml(id)}" name="${escapeHtml(name)}"`
        + ` class="form-select ts-select ${/spell|menta|curse/.test(suffix) ? 'spell_user_choice' : 'select_user_choice'} ${escapeHtml(family)}"`
        + ` data-show-icons="false" data-user-choice-layer="${escapeHtml(suffix)}" data-user-choice-selected="${escapeHtml(saved)}" data-ft-requires-level="${level}"`
        + (feat ? ` data-feat-source-id="${escapeHtml(sourceId)}" onchange="queryFeatsTable('${escapeHtml(sourceId)}')"`
            : essence ? ' onchange="calcEssences()"' : '') + '><option value="">-- SELECT --</option>';
    html += choices.map(choice => `<option value="${escapeHtml(choice.id)}"${creatorId(choice.id) === creatorId(saved) && saved ? ' selected' : ''}`
        + grants(choice.node, choice.id) + (hasLayer(choice.node, 'feat_repeatable') ? ' class="feat_repeatable"' : '')
        + `>${escapeHtml(choice.name)}</option>`).join('') + '</select>';
    if (feat) {
        const chosen = choices.find(choice => creatorId(choice.id) === creatorId(saved));
        const nested = chosen && !seen.has(sourceId) ? new Set([...seen, sourceId]) : null;
        html += `<div id="div_refresh_${escapeHtml(sourceId)}_feat">${chosen && nested
            ? choiceKeys(chosen.node).map(layer => formChoiceHtml(ctx, layer, owner, sourceId, inputs, chosen.node, nested)).join('') : ''}</div>`;
    }
    return html + '</li>';
}

function rowRenderer(input: LegacyListInput, inputs?: FormInputs): NonNullable<RenderCtx['renderRows']> {
    return (ctx, layer, entries, search, qty, leading) => entries.map(entry => {
        const node = entryRequirementResolver(ctx)(entry);
        const id = String(itemsCol(node, 'item_id') ?? scalarLayerSlot(entry));
        let html = renderCardListItemLayerRead(ctx, layer, [entry], search, qty, leading);
        const casterId = String(encounterCreatureField(ctx.arr.encounter_creatures, 'encounter_creature_id') ?? '');
        if (casterId && (layer === 'at_will_spell' || layer === 'innate_spell_per_long_rest')) {
            const controls = `<ft-ability-button><script type="application/json" class="ft-ability-button-input">${json({
                ability_id: id, caster_id: casterId, options: { spellType: layer, source: object(entry).source ?? null, listControls: true },
            })}</script></ft-ability-button>`;
            html = html.replace('<div class="d-flex align-items-center">', '<div class="d-flex align-items-center">' + controls);
        }
        if (!input.form || !inputs) return html;
        const grantRows = Object.entries(creatorSkillGrants(node, inputs.metadata.tiers ?? [])).map(([s, t]) => ({ s, t }));
        const activation = Number(layerOf(node, 'requires_level') ?? layerOf(node, 'requires_class_level') ?? 1);
        html = html.replace('<li ', `<li data-ft-skill-grants="${escapeHtml(JSON.stringify(grantRows))}" data-ft-grant-level="${Math.max(1, activation)}" data-ft-grant-ability="${escapeHtml(id)}" `);
        const parent = String(itemsCol(ctx.arr, 'item_id') ?? '');
        const raw = object(entry), candidate = raw.exact_choice_item_id ?? raw.source;
        const exact = isUuid(candidate) ? String(candidate) : id;
        html += choiceKeys(node).map(key => formChoiceHtml(ctx, key, parent, exact, inputs, node)).join('');
        if (/^skill_tier_\d+$/.test(layer) && ['background', 'class', 'subclass'].includes(String(itemsCol(ctx.arr, 'item_type') ?? '')))
            html += `<input type="hidden" class="${escapeHtml(itemsCol(ctx.arr, 'item_type'))}_skill_select" value="${escapeHtml(layer + '|' + id)}">`;
        if (String(itemsCol(node, 'item_type') ?? '').endsWith('_skill'))
            html += `<input type="hidden" class="select_user_choice ${escapeHtml(layer)}" value="${escapeHtml(id)}">`;
        return html;
    }).join('');
}

/** Reuses the list primitives; no stateful sheet module is imported into this bundle. */
export function renderLegacyListHtml(input: LegacyListInput, inputs?: FormInputs): string {
    let ctx = listDisplayContext(input.arr);
    const layer = String(input.layer);
    if (inputs) ctx = listDisplayContext({ ...input.arr, catalog: { ...inputs.catalog, ...object(input.arr.catalog) } });
    const options = authoredSlotOptions(ctx, layer, optionsOf(input.options ?? {}));
    ctx.renderRows = rowRenderer(input, inputs);
    if (input.form && inputs) {
        ctx.formClassLevel = input.class_level ?? undefined;
        ctx.renderChoice = (context, _choices, _suffix, key, owner) => formChoiceHtml(context, key, owner, '', inputs);
    }
    if (input.kind === 'single') return layer === 'training_book_tier' ? renderTrainingBookTier(ctx) : renderSingleItem(ctx, layer, options);
    if (input.kind === 'card') return renderCardListBlock(ctx, layer, { ...options, derivedEntries: [
        ...(layer === 'natural_weapon' ? (ctx.arr.computed?.creature?.natural_weapon as unknown[] ?? []) : []),
        ...(Array.isArray(options.derivedEntries) ? options.derivedEntries : []),
    ] });
    if (input.kind === 'compact') return renderCompactCardListLayers(ctx, input.layer as string[], options);
    if (input.kind === 'choices') return renderUserChoiceListRead(ctx, layer, options);
    if (input.kind === 'rows') return renderCardListItemLayerRead(ctx, layer, layerEntriesOf(ctx.arr, layer), !!options.sheetAbilitySearch, options.qtyFallback, options.leadingIcon as string);
    if (input.kind === 'ability-list') {
        const casterId = String(encounterCreatureField(ctx.arr.encounter_creatures, 'encounter_creature_id') ?? '');
        if (!input.form && casterId && itemsCol(ctx.arr, 'item_type') === 'creature')
            return `<div class="ft-pc-sheet-pane" data-ft-pc-pane="${layer === 'passive_ability' ? 'passive_abilities' : 'ability_section'}" data-ft-ability-type="${escapeHtml(layer)}" data-ft-caster-id="${escapeHtml(casterId)}"></div>`;
        return renderAbilityListRead(ctx, layer, { ...LIST_DISPLAY_ABILITY_CONFIG[layer]!, ...options } as typeof LIST_DISPLAY_ABILITY_CONFIG[string]);
    }
    if (input.kind === 'custom') {
        if (layer === 'skill_roll_mod') return renderSkillRollModifiers(ctx);
        if (options.authoredSlotPresentation) return renderCustomListBlock(ctx, layer, options);
        const rows = Array.isArray(options.rows) ? options.rows as Array<{ html: string; class: string }> : [];
        return renderCustomListBlock(ctx, layer, { ...options, items: rows, itemCallback: row => object(row).html ?? '',
            itemLiClassCallback: row => object(row).class ?? 'list-group-item' });
    }
    throw new Error('unported:list-display:unknown-mount:' + input.kind);
}

const states = new WeakMap<HTMLElement, { input: LegacyListInput; key?: string | null; pending: boolean; done?: boolean }>();
export async function mountLegacyListDisplay(el: HTMLElement): Promise<void> {
    const previous = states.get(el);
    const text = el.querySelector('.ft-list-display-input')?.textContent;
    if ((!text || text === previous?.key) && (previous?.pending || previous?.done)) return;
    if (!text && !previous) return;
    const state = { input: text ? JSON.parse(text) as LegacyListInput : previous!.input, key: text ?? previous?.key, pending: true, done: false };
    states.set(el, state);
    if (state.input.form && !el.querySelector('[data-ft-list-pending]')) {
        const pending = document.createElement('select');
        pending.className = 'select_user_choice'; pending.disabled = true; pending.hidden = true;
        pending.setAttribute('data-ft-list-pending', '1'); pending.setAttribute('data-user-choice-layer', 'options loading');
        el.append(pending);
    }
    try {
        state.input.arr = await compileFetchedEntity(state.input.arr) as RenderBlob;
        const inputs = state.input.form ? await formInputs(state.input) : undefined;
        if (!el.isConnected || states.get(el) !== state) return;
        el.innerHTML = renderLegacyListHtml(state.input, inputs);
        state.done = true;
        el.style.display = 'contents';
        host.ftMountPcSheet?.(el);
        host.ftMountAbilityButtons?.(el);
        host.ftHydrateUserChoiceOptions?.(el);
        host.ftMountRuneIcons?.(el);
        host.ftMountThumbnails?.(el);
        host.initializeTomSelect?.();
    } catch (error) {
        if (!el.isConnected) return;
        el.querySelector('[data-ft-list-error]')?.remove();
        const message = document.createElement('span');
        message.setAttribute('data-ft-list-error', '1'); message.className = 'text-danger';
        message.textContent = error instanceof Error ? error.message : 'List display unavailable. Please retry.';
        const retry = document.createElement('button'); retry.type = 'button'; retry.textContent = 'Retry';
        retry.addEventListener('click', () => { message.remove(); void mountLegacyListDisplay(el); });
        message.append(retry); el.append(message);
    } finally { state.pending = false; }
}

// Custom elements cover initial HTML, AJAX panes and form cards without a body-wide observer.
if (typeof customElements !== 'undefined' && !customElements.get('ft-list-display')) {
    customElements.define('ft-list-display', class extends HTMLElement {
        connectedCallback(): void {
            const mount = (): void => { void mountLegacyListDisplay(this); };
            if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', mount, { once: true });
            else queueMicrotask(mount);
        }
    });
}
